// ─────────────────────────────────────────────────────────────
// Clickbait ERP — Auth & RBAC Type Definitions
// ─────────────────────────────────────────────────────────────

// ── Roles ─────────────────────────────────────────────────────
export type Role = 'superadmin' | 'founder' | 'employee'

// ── Founder identities ────────────────────────────────────────
export type FounderKey = 'bunny' | 'hj' | 'darshu'

// ── Granular Permission Enum ──────────────────────────────────
export enum Permission {
  // User/access management (superadmin only by default)
  MANAGE_USERS        = 'MANAGE_USERS',
  EDIT_ACCESS_CONTROL = 'EDIT_ACCESS_CONTROL',
  VIEW_AUDIT_LOG      = 'VIEW_AUDIT_LOG',
  VIEW_ALL_DASHBOARDS = 'VIEW_ALL_DASHBOARDS',
  IMPERSONATE_USER    = 'IMPERSONATE_USER',

  // Clients
  VIEW_CLIENTS  = 'VIEW_CLIENTS',
  EDIT_CLIENTS  = 'EDIT_CLIENTS',

  // Pipeline / CRM
  VIEW_PIPELINE = 'VIEW_PIPELINE',
  EDIT_PIPELINE = 'EDIT_PIPELINE',

  // Invoices
  VIEW_INVOICES = 'VIEW_INVOICES',
  EDIT_INVOICES = 'EDIT_INVOICES',

  // Expenses
  VIEW_EXPENSES = 'VIEW_EXPENSES',
  EDIT_EXPENSES = 'EDIT_EXPENSES',

  // Campaigns / Ads
  VIEW_CAMPAIGNS       = 'VIEW_CAMPAIGNS',
  EDIT_CAMPAIGNS       = 'EDIT_CAMPAIGNS',
  VIEW_AD_PERFORMANCE  = 'VIEW_AD_PERFORMANCE',
  EDIT_AD_PERFORMANCE  = 'EDIT_AD_PERFORMANCE',

  // Content & Scheduling
  VIEW_CONTENT_CALENDAR  = 'VIEW_CONTENT_CALENDAR',
  EDIT_CONTENT_CALENDAR  = 'EDIT_CONTENT_CALENDAR',
  VIEW_SCHEDULING        = 'VIEW_SCHEDULING',
  EDIT_SCHEDULING        = 'EDIT_SCHEDULING',

  // Tasks & Employees
  ASSIGN_TASKS      = 'ASSIGN_TASKS',
  VIEW_EMPLOYEES    = 'VIEW_EMPLOYEES',
  MANAGE_EMPLOYEES  = 'MANAGE_EMPLOYEES',

  // Reports
  VIEW_REPORTS   = 'VIEW_REPORTS',
  EXPORT_REPORTS = 'EXPORT_REPORTS',

  // Projects
  VIEW_PROJECTS  = 'VIEW_PROJECTS',
  EDIT_PROJECTS  = 'EDIT_PROJECTS',

  // Dashboard variants
  VIEW_OPERATIONS_DASHBOARD  = 'VIEW_OPERATIONS_DASHBOARD',
  VIEW_AD_DASHBOARD          = 'VIEW_AD_DASHBOARD',
  VIEW_BUDGET_TRACKER        = 'VIEW_BUDGET_TRACKER',
}

// ── User ──────────────────────────────────────────────────────
export interface User {
  id: string
  name: string
  email: string
  role: Role
  founderKey?: FounderKey          // only for founders
  reportingTo?: string             // employee → founder user ID
  permissions: Permission[]
  isActive: boolean
  createdAt: string
  lastLogin?: string
  avatarInitials: string
}

// ── Session ───────────────────────────────────────────────────
export interface Session {
  user: User
  token: string                    // random UUID — real JWT when backend ready
  expiresAt: number                // Unix timestamp ms
  createdAt: number
}

// ── Login credentials ─────────────────────────────────────────
export interface LoginCredentials {
  email: string
  password: string
}

// ── Auth result ───────────────────────────────────────────────
export type AuthResult =
  | { success: true;  user: User }
  | { success: false; error: string }

// ── Auth context shape ────────────────────────────────────────
export interface AuthContextValue {
  currentUser: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<AuthResult>
  logout: () => void
  hasPermission: (permission: Permission) => boolean
  isRole: (role: Role | Role[]) => boolean
  updateUserPermissions: (userId: string, permissions: Permission[]) => void
  impersonating: User | null
  startImpersonation: (user: User) => void
  stopImpersonation: () => void
}

// ── Audit log entry ───────────────────────────────────────────
export interface AuditEntry {
  id: string
  userId: string
  userName: string
  userRole: Role
  action: string
  module: string
  detail: string
  oldValue?: string
  newValue?: string
  timestamp: number                // Unix ms
}
